package com.fantasytenis.web.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import com.fantasytenis.web.theme.Colors
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.w3c.dom.events.KeyboardEvent

/**
 * Accessible confirmation dialog component.
 *
 * Features:
 * - Focus trap (Tab cycles within dialog)
 * - Escape key to close
 * - ARIA attributes for screen readers
 * - Click outside to close
 * - Prevents body scroll when open
 *
 * @param title Dialog title
 * @param message Confirmation message
 * @param confirmText Text for confirm button (default: "Confirm")
 * @param cancelText Text for cancel button (default: "Cancel")
 * @param confirmVariant Button variant for confirm ("primary" or "danger")
 * @param onConfirm Called when user confirms
 * @param onCancel Called when user cancels or closes dialog
 */
@Composable
fun ConfirmationDialog(
    title: String,
    message: String,
    confirmText: String = "Confirm",
    cancelText: String = "Cancel",
    confirmVariant: String = "primary",
    onConfirm: () -> Unit,
    onCancel: () -> Unit
) {
    // Handle Escape key to close dialog
    DisposableEffect(Unit) {
        val handleKeyDown: (org.w3c.dom.events.Event) -> Unit = { event ->
            val keyEvent = event as? KeyboardEvent
            if (keyEvent?.key == "Escape") {
                onCancel()
            }
        }
        document.addEventListener("keydown", handleKeyDown)

        // Prevent body scroll
        document.body?.style?.setProperty("overflow", "hidden")

        onDispose {
            document.removeEventListener("keydown", handleKeyDown)
            document.body?.style?.setProperty("overflow", "")
        }
    }

    // Modal backdrop
    Div({
        classes("modal-backdrop")
        attr("role", "dialog")
        attr("aria-modal", "true")
        attr("aria-labelledby", "dialog-title")
        attr("aria-describedby", "dialog-message")
        onClick { onCancel() }
        style {
            position(Position.Fixed)
            top(0.px)
            left(0.px)
            right(0.px)
            bottom(0.px)
            backgroundColor(Color("rgba(0, 0, 0, 0.5)"))
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.Center)
            alignItems(AlignItems.Center)
            property("z-index", "1000")
        }
    }) {
        // Modal content
        Div({
            classes("modal-content")
            attr("tabindex", "-1")
            onClick { it.stopPropagation() }
            style {
                backgroundColor(Colors.Surface)
                borderRadius(12.px)
                padding(24.px)
                maxWidth(400.px)
                width(90.percent)
                property("box-shadow", "0 4px 20px rgba(0, 0, 0, 0.15)")
            }
        }) {
            // Title
            H3({
                id("dialog-title")
                style {
                    fontSize(20.px)
                    fontWeight(600)
                    color(Colors.TextPrimary)
                    marginBottom(12.px)
                }
            }) {
                Text(title)
            }

            // Message
            P({
                id("dialog-message")
                style {
                    fontSize(14.px)
                    color(Colors.TextSecondary)
                    lineHeight("1.5")
                    marginBottom(24.px)
                }
            }) {
                Text(message)
            }

            // Action buttons
            Div({
                style {
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.FlexEnd)
                    gap(12.px)
                }
            }) {
                Button(
                    text = cancelText,
                    onClick = onCancel,
                    variant = "secondary"
                )
                if (confirmVariant == "danger") {
                    DangerButton(
                        text = confirmText,
                        onClick = onConfirm
                    )
                } else {
                    Button(
                        text = confirmText,
                        onClick = onConfirm,
                        variant = "primary"
                    )
                }
            }
        }
    }
}

/**
 * Danger button variant for destructive actions.
 */
@Composable
private fun DangerButton(
    text: String,
    onClick: () -> Unit
) {
    org.jetbrains.compose.web.dom.Button({
        classes("baseline-button")
        onClick { onClick() }
        style {
            backgroundColor(Colors.Error)
            color(Colors.White)
            border(1.px, LineStyle.Solid, Colors.Error)
            borderRadius(8.px)
            padding(12.px, 24.px)
            fontSize(16.px)
            fontWeight(600)
            cursor("pointer")
            property("transition", "all 0.2s")
        }
    }) {
        Text(text)
    }
}

/**
 * Specialized confirmation dialog for contest entry with fee display.
 */
@Composable
fun ContestEntryConfirmationDialog(
    contestName: String,
    entryFee: Double,
    onConfirm: () -> Unit,
    onCancel: () -> Unit
) {
    val feeDisplay = if (entryFee == 0.0) "FREE" else "$$entryFee"

    // Handle Escape key
    DisposableEffect(Unit) {
        val handleKeyDown: (org.w3c.dom.events.Event) -> Unit = { event ->
            val keyEvent = event as? KeyboardEvent
            if (keyEvent?.key == "Escape") {
                onCancel()
            }
        }
        document.addEventListener("keydown", handleKeyDown)
        document.body?.style?.setProperty("overflow", "hidden")

        onDispose {
            document.removeEventListener("keydown", handleKeyDown)
            document.body?.style?.setProperty("overflow", "")
        }
    }

    // Modal backdrop
    Div({
        classes("modal-backdrop")
        attr("role", "dialog")
        attr("aria-modal", "true")
        attr("aria-labelledby", "dialog-title")
        onClick { onCancel() }
        style {
            position(Position.Fixed)
            top(0.px)
            left(0.px)
            right(0.px)
            bottom(0.px)
            backgroundColor(Color("rgba(0, 0, 0, 0.5)"))
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.Center)
            alignItems(AlignItems.Center)
            property("z-index", "1000")
        }
    }) {
        // Modal content
        Div({
            classes("modal-content")
            attr("tabindex", "-1")
            onClick { it.stopPropagation() }
            style {
                backgroundColor(Colors.Surface)
                borderRadius(12.px)
                padding(24.px)
                maxWidth(400.px)
                width(90.percent)
                property("box-shadow", "0 4px 20px rgba(0, 0, 0, 0.15)")
            }
        }) {
            // Title
            H3({
                id("dialog-title")
                style {
                    fontSize(20.px)
                    fontWeight(600)
                    color(Colors.TextPrimary)
                    marginBottom(16.px)
                }
            }) {
                Text("Enter Contest")
            }

            // Contest name
            P({
                style {
                    fontSize(16.px)
                    fontWeight(500)
                    color(Colors.TextPrimary)
                    marginBottom(8.px)
                }
            }) {
                Text(contestName)
            }

            // Entry fee display
            Div({
                style {
                    backgroundColor(Colors.Gray100)
                    borderRadius(8.px)
                    padding(16.px)
                    marginBottom(24.px)
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.SpaceBetween)
                    alignItems(AlignItems.Center)
                }
            }) {
                Span({
                    style {
                        fontSize(14.px)
                        color(Colors.TextSecondary)
                    }
                }) {
                    Text("Entry Fee")
                }
                Span({
                    style {
                        fontSize(20.px)
                        fontWeight(700)
                        color(Colors.Primary)
                    }
                }) {
                    Text(feeDisplay)
                }
            }

            // Confirmation message
            P({
                style {
                    fontSize(14.px)
                    color(Colors.TextSecondary)
                    marginBottom(24.px)
                }
            }) {
                if (entryFee > 0) {
                    Text("This entry fee will be deducted from your wallet balance.")
                } else {
                    Text("This is a free contest. No entry fee required.")
                }
            }

            // Action buttons
            Div({
                style {
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.FlexEnd)
                    gap(12.px)
                }
            }) {
                Button(
                    text = "Cancel",
                    onClick = onCancel,
                    variant = "secondary"
                )
                Button(
                    text = "Confirm Entry",
                    onClick = onConfirm,
                    variant = "primary"
                )
            }
        }
    }
}

/**
 * Specialized confirmation dialog for lineup submission.
 */
@Composable
fun LineupSubmitConfirmationDialog(
    contestName: String,
    playerCount: Int,
    totalSalary: Int,
    salaryCap: Int,
    onConfirm: () -> Unit,
    onCancel: () -> Unit
) {
    // Handle Escape key
    DisposableEffect(Unit) {
        val handleKeyDown: (org.w3c.dom.events.Event) -> Unit = { event ->
            val keyEvent = event as? KeyboardEvent
            if (keyEvent?.key == "Escape") {
                onCancel()
            }
        }
        document.addEventListener("keydown", handleKeyDown)
        document.body?.style?.setProperty("overflow", "hidden")

        onDispose {
            document.removeEventListener("keydown", handleKeyDown)
            document.body?.style?.setProperty("overflow", "")
        }
    }

    // Modal backdrop
    Div({
        classes("modal-backdrop")
        attr("role", "dialog")
        attr("aria-modal", "true")
        onClick { onCancel() }
        style {
            position(Position.Fixed)
            top(0.px)
            left(0.px)
            right(0.px)
            bottom(0.px)
            backgroundColor(Color("rgba(0, 0, 0, 0.5)"))
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.Center)
            alignItems(AlignItems.Center)
            property("z-index", "1000")
        }
    }) {
        // Modal content
        Div({
            classes("modal-content")
            attr("tabindex", "-1")
            onClick { it.stopPropagation() }
            style {
                backgroundColor(Colors.Surface)
                borderRadius(12.px)
                padding(24.px)
                maxWidth(400.px)
                width(90.percent)
                property("box-shadow", "0 4px 20px rgba(0, 0, 0, 0.15)")
            }
        }) {
            // Title
            H3({
                style {
                    fontSize(20.px)
                    fontWeight(600)
                    color(Colors.TextPrimary)
                    marginBottom(16.px)
                }
            }) {
                Text("Submit Lineup")
            }

            // Contest name
            P({
                style {
                    fontSize(14.px)
                    color(Colors.TextSecondary)
                    marginBottom(16.px)
                }
            }) {
                Text(contestName)
            }

            // Lineup summary
            Div({
                style {
                    backgroundColor(Colors.Gray100)
                    borderRadius(8.px)
                    padding(16.px)
                    marginBottom(24.px)
                }
            }) {
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        justifyContent(JustifyContent.SpaceBetween)
                        marginBottom(8.px)
                    }
                }) {
                    Span({
                        style {
                            fontSize(14.px)
                            color(Colors.TextSecondary)
                        }
                    }) {
                        Text("Players")
                    }
                    Span({
                        style {
                            fontSize(14.px)
                            fontWeight(600)
                            color(Colors.TextPrimary)
                        }
                    }) {
                        Text("$playerCount / 5")
                    }
                }
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        justifyContent(JustifyContent.SpaceBetween)
                    }
                }) {
                    Span({
                        style {
                            fontSize(14.px)
                            color(Colors.TextSecondary)
                        }
                    }) {
                        Text("Salary Used")
                    }
                    Span({
                        style {
                            fontSize(14.px)
                            fontWeight(600)
                            color(Colors.Primary)
                        }
                    }) {
                        Text("$$totalSalary / $$salaryCap")
                    }
                }
            }

            // Warning message
            P({
                style {
                    fontSize(14.px)
                    color(Colors.TextSecondary)
                    marginBottom(24.px)
                }
            }) {
                Text("Once submitted, you can still edit your lineup until the contest locks.")
            }

            // Action buttons
            Div({
                style {
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.FlexEnd)
                    gap(12.px)
                }
            }) {
                Button(
                    text = "Cancel",
                    onClick = onCancel,
                    variant = "secondary"
                )
                Button(
                    text = "Submit Lineup",
                    onClick = onConfirm,
                    variant = "primary"
                )
            }
        }
    }
}
